/**
 * RemotePlayers - os OUTROS jogadores da partida online, dentro da cena.
 *
 * Um BotModel por jogador remoto: o boneco com esqueleto, IK de perna e
 * hitboxes ja existe e e o mesmo que a campanha usa. Aqui nao ha IA nenhuma -
 * o corpo apenas OBEDECE ao que o servidor manda, e a unica inteligencia do
 * modulo e reconstruir movimento continuo a partir de 20 fotos por segundo.
 *
 * Como o movimento e reconstruido:
 *   1. Cada snapshot entra num buffer circular por jogador, com o `ts` do
 *      relogio do SERVIDOR (nunca o local: relogios diferentes nao batem).
 *   2. O desenho acontece SEMPRE em (agora - NET.INTERP_DELAY), atrasado de
 *      proposito, para existirem dois snapshots cercando o instante desenhado.
 *      Interpola posicao linearmente e yaw pelo caminho CURTO.
 *   3. Sem snapshot futuro (perda de pacote), extrapola pela ultima velocidade
 *      por no maximo NET.MAX_EXTRAPOLATION e depois CONGELA. Extrapolar sem
 *      limite manda o boneco atravessar parede e sumir no horizonte.
 *
 * Vida, morte e pontuacao NAO se decidem aqui: o servidor e a verdade. O
 * `onDamage` registrado em ctx.targets existe apenas para a Ballistics ter um
 * canal de acerto (quem transforma isso em reclamacao ao servidor e o NetSync).
 *
 * Padroes do projeto seguidos: nada e importado para pegar instancia (tudo vem
 * de ctx), toda chamada em vizinho usa optional chaining, nada lanca dentro do
 * frame e os vetores temporarios sao de modulo (zero alocacao por frame).
 */

import * as THREE from 'three';
import { TEAM, PLAYER } from '../core/Constants.js';
import { NET } from '../../shared/netconfig.js';
import { FLAG, hasFlag } from '../../shared/protocol.js';
import { BotModel } from '../ai/BotModel.js';

/** Amostras guardadas por jogador: 32 a 20 Hz = 1,6 s de historico. */
const BUFFER_SIZE = 32;

/** Segundos que o corpo fica deitado antes de comecar a afundar. */
const DEATH_LINGER = 4.0;

/** Duracao do afundamento (sumir de uma vez pisca objeto na tela). */
const SINK_TIME = 1.4;

/** Profundidade final do afundamento, em metros. */
const SINK_DEPTH = 1.4;

/** Teto da velocidade derivada de dois snapshots (acima disso e teleporte). */
const MAX_DERIVED_SPEED = 14;

/** Diferenca de relogio que ja nao vale corrigir devagar (aba em background). */
const CLOCK_SNAP_MS = 300;

/** Ganho da correcao suave do relogio de interpolacao, por segundo. */
const CLOCK_CATCHUP = 2.0;

/** Velocidade maxima de giro do CORPO. O que sobra vira torcao de tronco. */
const TURN_BODY = 9.0;

/** Limite da torcao de tronco (o mesmo espirito do bot: 0.95 rad). */
const MAX_TORSO_TWIST = 0.9;

/** Suavizacao da velocidade que alimenta o ciclo de passo. */
const SPEED_SMOOTH = 12;

const TAU = Math.PI * 2;

// --- temporarios de modulo (nenhuma alocacao dentro de update) --------------
const _pos = new THREE.Vector3();
const _sv = {
  yaw: 0, pitch: 0, flags: 0, weaponIndex: 0,
  hp: PLAYER.MAX_HEALTH, alive: true, speed: 0,
  vx: 0, vy: 0, vz: 0
};

/** Diferenca de angulo pelo caminho curto: sem isto o boneco gira o mundo
 *  inteiro ao cruzar +-180 graus. */
function shortAngle(a) {
  let d = a % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
}

function wrapAngle(a) {
  let v = a % TAU;
  if (v > Math.PI) v -= TAU;
  if (v < -Math.PI) v += TAU;
  return v;
}

function clampAbs(v, limit) {
  return v > limit ? limit : (v < -limit ? -limit : v);
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

function nowMs() {
  return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

function createSample() {
  return {
    t: 0, x: 0, y: 0, z: 0, yaw: 0, pitch: 0,
    flags: 0, weaponIndex: 0, hp: PLAYER.MAX_HEALTH, alive: true,
    vx: 0, vy: 0, vz: 0, speed: 0
  };
}

export class RemotePlayers {
  constructor(ctx) {
    this.ctx = ctx;

    /** @type {Map<number, object>} id -> entidade remota */
    this._byId = new Map();
    /** Lista paralela ao Map: iteracao por indice, sem alocar iterador. */
    this._list = [];

    /** id do jogador LOCAL: ele nunca entra como remoto. */
    this._localId = null;

    this._time = 0;             // relogio local acumulado (segundos)
    this._latestTs = 0;         // maior ts de servidor ja recebido (ms)
    this._renderTs = 0;         // instante que esta sendo desenhado (ms, servidor)
    this._warned = new Set();
  }

  async init() {
    // Nasce vazio e cresce com os playerJoin/snapshots. Nada pesado aqui: o
    // custo de um remoto (montar o BotModel) so aparece quando ele entra.
  }

  // =========================================================================
  // registro de jogadores
  // =========================================================================

  /** Quem sou eu na partida. O proprio jogador nao pode virar boneco remoto. */
  setLocalId(id) {
    this._localId = Number.isFinite(id) ? (id | 0) : null;
    if (this._localId !== null && this._byId.has(this._localId)) this.remove(this._localId);
  }

  /**
   * Entra um jogador remoto. Idempotente: chamado tanto pelo `playerJoin` como
   * pelo primeiro snapshot em que o id aparece (a ordem das duas mensagens nao
   * e garantida, e um boneco duplicado seria pior).
   * @param {{id:number, nickname?:string, name?:string, team?:number}} playerInfo
   */
  add(playerInfo) {
    const id = playerInfo && Number.isFinite(playerInfo.id) ? (playerInfo.id | 0) : null;
    if (id === null) return null;
    if (this._localId !== null && id === this._localId) return null;

    const existing = this._byId.get(id);
    if (existing) {
      const nick = playerInfo.nickname || playerInfo.name;
      if (nick) {
        existing.nickname = String(nick);
        existing.name = existing.nickname;
      }
      return existing;
    }

    let model = null;
    try {
      // variante e semente derivadas do id: o mesmo jogador tem sempre o mesmo
      // uniforme, em todas as maquinas, sem precisar mandar isso pelo fio.
      model = new BotModel(Math.abs(id) % 3, Math.abs(id) * 7919 + 13);
    } catch (err) {
      this._warnOnce('BotModel', err);
      return null;
    }

    const nickname = String(playerInfo.nickname || playerInfo.name || `JOGADOR ${id}`);
    const remote = {
      id,
      /** O killfeed do HUD le `.name`. */
      name: nickname,
      nickname,
      isRemote: true,
      isBot: false,
      team: TEAM.ENEMY,

      model,
      hitboxes: model.hitboxes,

      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      yaw: 0,
      pitch: 0,
      bodyYaw: 0,
      flags: 0,
      weaponIndex: 0,
      speed: 0,
      eyeHeight: PLAYER.EYE_HEIGHT,

      health: PLAYER.MAX_HEALTH,
      maxHealth: PLAYER.MAX_HEALTH,
      alive: true,
      registered: false,
      deathAt: -1,

      /** Ultimo acerto local reclamado (diagnostico; nao aplica dano). */
      lastHit: { amount: 0, part: 'chest', at: 0 },

      // buffer circular de snapshots
      buf: new Array(BUFFER_SIZE),
      n: 0,
      i: -1,
      lastTs: 0,
      lastRecvAt: 0,
      spawned: false,

      // pose reaproveitada entregue ao BotModel (nunca um objeto novo por frame)
      pose: { speed: 0, crouch: 0, aim: 0, torsoYaw: 0, pitch: 0, scanYaw: 0, reload: 0 },

      onDamage: null
    };
    for (let k = 0; k < BUFFER_SIZE; k++) remote.buf[k] = createSample();

    // Conta o silencio desde JA: jogador que entrou e nunca mandou estado
    // (travou no carregamento) sai pelo mesmo caminho de quem caiu.
    remote.lastRecvAt = this._time;

    remote.onDamage = (amount, info) => this._onLocalHit(remote, amount, info);

    // Marca a entidade nas hitboxes: FX e Ballistics resolvem "de quem e este
    // mesh?" por userData.entity, do mesmo jeito que fazem com o bot.
    for (let k = 0; k < remote.hitboxes.length; k++) {
      const hb = remote.hitboxes[k];
      if (hb?.object) hb.object.userData.entity = remote;
    }

    try {
      model.setVisible(false);   // so aparece quando houver posicao real do servidor
      this.ctx.scene?.add?.(model.root);
    } catch (err) {
      this._warnOnce('scene.add', err);
    }

    this._byId.set(id, remote);
    this._list.push(remote);
    // O registro em ctx.targets NAO acontece aqui: sem posicao do servidor o
    // boneco estaria na origem do mundo, e um alvo invisivel na origem levaria
    // tiro. Entra no registro no primeiro snapshot (ver _push).
    return remote;
  }

  /** Sai da partida: fora da cena, fora do registro de alvos, modelo liberado. */
  remove(id) {
    const key = Number.isFinite(id) ? (id | 0) : null;
    if (key === null) return false;
    const r = this._byId.get(key);
    if (!r) return false;

    this._unregister(r);
    this._byId.delete(key);
    const idx = this._list.indexOf(r);
    if (idx >= 0) this._list.splice(idx, 1);

    try {
      r.model?.root?.parent?.remove(r.model.root);
      r.model?.dispose?.();
    } catch (err) {
      this._warnOnce('dispose', err);
    }
    return true;
  }

  clear() {
    for (let i = this._list.length - 1; i >= 0; i--) this.remove(this._list[i].id);
    this._byId.clear();
    this._list.length = 0;
    this._latestTs = 0;
    this._renderTs = 0;
  }

  get(id) {
    return this._byId.get(Number.isFinite(id) ? (id | 0) : -1) || null;
  }

  /** Array INTERNO reaproveitado - trate como somente leitura. */
  list() {
    return this._list;
  }

  count() {
    return this._list.length;
  }

  // =========================================================================
  // entrada de rede
  // =========================================================================

  /**
   * Guarda um snapshot no buffer de interpolacao de cada remoto citado.
   * @param {number} ts  relogio do SERVIDOR, em milissegundos
   * @param {Array<{id:number,p:number[],y:number,pt:number,f:number,w:number,hp:number,alive:boolean}>} players
   */
  applySnapshot(ts, players) {
    if (!Array.isArray(players) || players.length === 0) return;
    const stamp = this._stampFor(ts);
    if (stamp > this._latestTs) this._latestTs = stamp;

    for (let i = 0; i < players.length; i++) {
      const s = players[i];
      if (!s || !Number.isFinite(s.id)) continue;
      const id = s.id | 0;
      if (this._localId !== null && id === this._localId) continue;

      let r = this._byId.get(id);
      // Snapshot pode chegar antes do playerJoin (ou depois de um reconnect):
      // ignorar aqui deixaria um jogador invisivel para sempre.
      if (!r) r = this.add({ id, nickname: s.nickname });
      if (!r) continue;

      this._push(r, stamp, s);
    }
  }

  /**
   * Morte anunciada pelo servidor. Vem antes ou depois do snapshot com
   * `alive:false` - os dois caminhos levam a mesma morte, e o segundo nao
   * repete a animacao (o BotModel ignora playDeath quando ja esta morto).
   */
  kill(id, killerId, headshot) {
    const r = this.get(id);
    if (!r) return false;
    let dx = Math.sin(r.bodyYaw);
    let dz = Math.cos(r.bodyYaw);
    const killer = this._killerPosition(killerId);
    if (killer) {
      // corpo tomba PARA o lado de onde veio o tiro, nao para um lado sorteado
      dx = r.position.x - killer.x;
      dz = r.position.z - killer.z;
    }
    this._die(r, dx, dz, !!headshot);
    return true;
  }

  /** Respawn: o corpo volta inteiro, no lugar que o servidor mandou. */
  respawn(id, pos, yaw, hp) {
    const r = this.get(id);
    if (!r) return false;

    if (Array.isArray(pos) && pos.length >= 3) {
      r.position.set(num(+pos[0], 0), num(+pos[1], 0), num(+pos[2], 0));
    } else if (pos && Number.isFinite(pos.x)) {
      r.position.set(pos.x, num(pos.y, 0), pos.z);
    }
    r.yaw = num(+yaw, r.yaw);
    r.bodyYaw = r.yaw;
    r.pitch = 0;
    r.velocity.set(0, 0, 0);
    r.speed = 0;
    r.health = Number.isFinite(hp) ? hp : r.maxHealth;
    r.alive = true;
    r.deathAt = -1;
    r.flags = 0;

    // Buffer zerado: as amostras de antes da morte estao em outro lugar do mapa
    // e interpolar entre elas e a nova posicao arrastaria o boneco pelo cenario.
    r.n = 0;
    r.i = -1;
    r.lastTs = 0;

    try {
      r.model.resetPose();
      r.model.setSink(0);
      r.model.root.position.copy(r.position);
      r.model.root.rotation.set(0, r.bodyYaw, 0);
      r.model.setVisible(true);
    } catch (err) {
      this._warnOnce('respawn', err);
    }
    this._register(r);
    return true;
  }

  // =========================================================================
  // laco
  // =========================================================================

  update(dt) {
    try {
      const step = Number.isFinite(dt) && dt > 0 ? dt : 0;
      this._time += step;

      const hasClock = this._advanceClock(step);
      const renderTs = this._renderTs;

      for (let i = this._list.length - 1; i >= 0; i--) {
        const r = this._list[i];

        // Sem snapshot ha NET.REMOTE_TIMEOUT: o jogador sumiu (queda de conexao
        // sem o `playerLeave` chegar). Some da tela em vez de virar estatua.
        if (this._time - r.lastRecvAt > NET.REMOTE_TIMEOUT) {
          this.remove(r.id);
          continue;
        }
        this._advanceRemote(r, step, renderTs, hasClock);
      }
    } catch (err) {
      this._warnOnce('update', err);
    }
  }

  dispose() {
    this.clear();
  }

  // =========================================================================
  // interno - relogio de interpolacao
  // =========================================================================

  /**
   * O instante desenhado caminha com o frame e e puxado devagar para
   * (ultimo snapshot - INTERP_DELAY). Puxar de uma vez a cada snapshot faria o
   * boneco tremer; nunca puxar acumularia atraso ate a extrapolacao estourar.
   * @returns {boolean} ha relogio utilizavel (ou seja: ja chegou algum snapshot)
   */
  _advanceClock(dt) {
    if (this._latestTs <= 0) return false;

    const target = this._latestTs - NET.INTERP_DELAY * 1000;
    if (this._renderTs <= 0) {
      this._renderTs = target;
      return true;
    }
    this._renderTs += dt * 1000;

    const drift = target - this._renderTs;
    if (drift > CLOCK_SNAP_MS || drift < -CLOCK_SNAP_MS) this._renderTs = target;
    else this._renderTs += drift * Math.min(1, dt * CLOCK_CATCHUP);
    return true;
  }

  /**
   * Carimbo da amostra. O `ts` do servidor e a fonte correta; sem ele (servidor
   * antigo ou campo perdido) a cadencia nominal do snapshot mantem a ordem, que
   * e o minimo que a interpolacao precisa.
   */
  _stampFor(ts) {
    if (Number.isFinite(ts) && ts > 0) return ts;
    if (this._latestTs > 0) return this._latestTs + 1000 / NET.SNAPSHOT_RATE;
    return nowMs();
  }

  // =========================================================================
  // interno - buffer
  // =========================================================================

  _push(r, stamp, s) {
    const p = s.p;
    if (!Array.isArray(p) || p.length < 3) return;
    const x = +p[0], y = +p[1], z = +p[2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;

    // Snapshot fora de ordem e DESCARTADO: aplicar estado velho depois do novo
    // faz o boneco voltar no tempo na tela.
    if (r.n > 0 && stamp <= r.lastTs) return;

    const prev = r.n > 0 ? r.buf[r.i] : null;
    r.i = (r.i + 1) % BUFFER_SIZE;
    if (r.n < BUFFER_SIZE) r.n++;
    const sm = r.buf[r.i];

    sm.t = stamp;
    sm.x = x; sm.y = y; sm.z = z;
    sm.yaw = num(+s.y, prev ? prev.yaw : 0);
    sm.pitch = num(+s.pt, 0);
    sm.flags = Number.isFinite(s.f) ? (s.f | 0) : 0;
    sm.weaponIndex = Number.isFinite(s.w) ? (s.w | 0) : 0;
    sm.hp = Number.isFinite(s.hp) ? s.hp : r.maxHealth;
    sm.alive = s.alive !== false && sm.hp > 0;

    // Velocidade derivada de dois snapshots: e ela que sustenta a extrapolacao
    // quando um pacote se perde, e tambem o `speed` do ciclo de passo.
    if (prev) {
      const h = (stamp - prev.t) / 1000;
      if (h > 1e-3) {
        let vx = (x - prev.x) / h;
        let vy = (y - prev.y) / h;
        let vz = (z - prev.z) / h;
        const horiz = Math.hypot(vx, vz);
        if (horiz > MAX_DERIVED_SPEED) {
          // teleporte (respawn ou correcao do servidor): nao vira velocidade
          vx = 0; vy = 0; vz = 0;
          sm.speed = 0;
        } else {
          sm.speed = horiz;
        }
        sm.vx = vx; sm.vy = vy; sm.vz = vz;
      } else {
        sm.vx = prev.vx; sm.vy = prev.vy; sm.vz = prev.vz;
        sm.speed = prev.speed;
      }
    } else {
      sm.vx = 0; sm.vy = 0; sm.vz = 0;
      sm.speed = 0;
    }

    r.lastTs = stamp;
    r.lastRecvAt = this._time;
    r.weaponIndex = sm.weaponIndex;

    if (!r.spawned) {
      // Primeira posicao real: agora da para mostrar o boneco sem ele piscar
      // na origem do mundo.
      r.spawned = true;
      r.position.set(x, y, z);
      r.yaw = sm.yaw;
      r.bodyYaw = sm.yaw;
      try {
        r.model.root.position.copy(r.position);
        r.model.root.rotation.set(0, r.bodyYaw, 0);
        r.model.setVisible(true);
      } catch (err) {
        this._warnOnce('spawn', err);
      }
      if (sm.alive) this._register(r);
    }
  }

  /**
   * Resolve a pose do instante `renderTs` escrevendo em _pos e _sv.
   * @returns {boolean} conseguiu resolver
   */
  _solve(r, renderTs) {
    const n = r.n;
    if (n === 0) return false;

    // Procura, do mais novo para o mais velho, a amostra que ainda esta no
    // passado do instante desenhado.
    let s0 = null;
    let s1 = null;
    for (let k = 0; k < n; k++) {
      const s = r.buf[(r.i - k + BUFFER_SIZE) % BUFFER_SIZE];
      if (s.t <= renderTs) {
        s0 = s;
        if (k > 0) s1 = r.buf[(r.i - (k - 1) + BUFFER_SIZE) % BUFFER_SIZE];
        break;
      }
    }

    if (!s0) {
      // Todo o buffer esta no futuro (acabou de entrar na sala): mostra a
      // amostra mais antiga em vez de inventar posicao.
      const oldest = r.buf[(r.i - (n - 1) + BUFFER_SIZE) % BUFFER_SIZE];
      _pos.set(oldest.x, oldest.y, oldest.z);
      _sv.yaw = oldest.yaw;
      _sv.pitch = oldest.pitch;
      _sv.flags = oldest.flags;
      _sv.weaponIndex = oldest.weaponIndex;
      _sv.hp = oldest.hp;
      _sv.alive = oldest.alive;
      _sv.speed = 0;
      _sv.vx = 0; _sv.vy = 0; _sv.vz = 0;
      return true;
    }

    if (s1 && s1.t > s0.t) {
      const a = Math.min(1, Math.max(0, (renderTs - s0.t) / (s1.t - s0.t)));
      _pos.set(
        s0.x + (s1.x - s0.x) * a,
        s0.y + (s1.y - s0.y) * a,
        s0.z + (s1.z - s0.z) * a
      );
      // yaw pelo caminho CURTO: interpolar o valor cru gira o boneco o mundo
      // inteiro ao cruzar +-180 graus.
      _sv.yaw = wrapAngle(s0.yaw + shortAngle(s1.yaw - s0.yaw) * a);
      _sv.pitch = s0.pitch + (s1.pitch - s0.pitch) * a;
      // O estado mais NOVO manda nas flags e na vida: piscar ADS/agachado no
      // meio da interpolacao seria pior que atrasar um snapshot.
      _sv.flags = s1.flags;
      _sv.weaponIndex = s1.weaponIndex;
      _sv.hp = s1.hp;
      _sv.alive = s1.alive;
      // velocidade do TRECHO desenhado: distancia percorrida / tempo do trecho
      const h = (s1.t - s0.t) / 1000;
      if (h > 1e-4) {
        _sv.vx = (s1.x - s0.x) / h;
        _sv.vy = (s1.y - s0.y) / h;
        _sv.vz = (s1.z - s0.z) / h;
        _sv.speed = Math.hypot(_sv.vx, _sv.vz);
      } else {
        _sv.vx = 0; _sv.vy = 0; _sv.vz = 0;
        _sv.speed = 0;
      }
      return true;
    }

    // Sem amostra futura: extrapola pela ultima velocidade, no maximo
    // NET.MAX_EXTRAPOLATION, e entao congela.
    const ahead = Math.min(NET.MAX_EXTRAPOLATION, Math.max(0, (renderTs - s0.t) / 1000));
    _pos.set(s0.x + s0.vx * ahead, s0.y + s0.vy * ahead, s0.z + s0.vz * ahead);
    _sv.yaw = s0.yaw;
    _sv.pitch = s0.pitch;
    _sv.flags = s0.flags;
    _sv.weaponIndex = s0.weaponIndex;
    _sv.hp = s0.hp;
    _sv.alive = s0.alive;
    // Congelado (passou da janela de extrapolacao): para de "andar" tambem na
    // animacao, senao o boneco patina parado no lugar.
    const frozen = (renderTs - s0.t) / 1000 > NET.MAX_EXTRAPOLATION;
    _sv.speed = frozen ? 0 : s0.speed;
    _sv.vx = frozen ? 0 : s0.vx;
    _sv.vy = frozen ? 0 : s0.vy;
    _sv.vz = frozen ? 0 : s0.vz;
    return true;
  }

  // =========================================================================
  // interno - por jogador, por frame
  // =========================================================================

  _advanceRemote(r, dt, renderTs, hasClock) {
    if (hasClock && r.n > 0 && this._solve(r, renderTs)) {
      r.position.copy(_pos);
      r.yaw = _sv.yaw;
      r.pitch = _sv.pitch;
      r.flags = _sv.flags;
      r.weaponIndex = _sv.weaponIndex;
      r.health = _sv.hp;
      r.velocity.set(_sv.vx, _sv.vy, _sv.vz);
      r.speed += (_sv.speed - r.speed) * Math.min(1, dt * SPEED_SMOOTH);

      if (_sv.alive === false) {
        if (r.alive) this._die(r, Math.sin(r.bodyYaw), Math.cos(r.bodyYaw), false);
      } else if (!r.alive) {
        // Reviveu sem mensagem de respawn (reconexao, ou a mensagem se perdeu).
        this.respawn(r.id, [r.position.x, r.position.y, r.position.z], r.yaw, _sv.hp);
      }
    }

    const model = r.model;
    if (!model) return;

    try {
      model.root.position.copy(r.position);

      if (!model.dead) {
        // O CORPO gira com limite de velocidade e a diferenca que sobra vira
        // torcao de tronco - e o que faz o boneco "olhar antes de virar", em vez
        // de girar em bloco como um poste.
        const diff = shortAngle(r.yaw - r.bodyYaw);
        const maxStep = TURN_BODY * dt;
        r.bodyYaw = wrapAngle(Math.abs(diff) <= maxStep ? r.yaw : r.bodyYaw + Math.sign(diff) * maxStep);
        model.root.rotation.set(0, r.bodyYaw, 0);
      }

      const p = r.pose;
      p.speed = r.speed;
      p.crouch = hasFlag(r.flags, FLAG.CROUCH) ? 1 : 0;
      p.aim = hasFlag(r.flags, FLAG.ADS) ? 1 : 0;
      p.torsoYaw = clampAbs(shortAngle(r.yaw - r.bodyYaw), MAX_TORSO_TWIST);
      p.pitch = r.pitch;
      p.scanYaw = 0;
      p.reload = hasFlag(r.flags, FLAG.RELOAD) ? 1 : 0;
      model.update(dt, p);

      if (!r.alive && r.deathAt >= 0) {
        const age = this._time - r.deathAt;
        if (age > DEATH_LINGER) {
          const k = (age - DEATH_LINGER) / SINK_TIME;
          if (k >= 1) model.setVisible(false);
          else model.setSink(k * SINK_DEPTH);
        }
      }
    } catch (err) {
      this._warnOnce('modelo', err);
    }
  }

  // =========================================================================
  // interno - vida e alvo
  // =========================================================================

  _die(r, dirX, dirZ, headshot) {
    if (!r.alive) return;
    r.alive = false;
    r.health = 0;
    r.speed = 0;
    r.deathAt = this._time;

    // Cadaver nao para bala: sair de ctx.targets AGORA (o registro tambem pula
    // quem tem alive === false, mas nao ha por que deixar a entrada la).
    this._unregister(r);

    try {
      r.model.playDeath(
        Number.isFinite(dirX) ? dirX : Math.sin(r.bodyYaw),
        Number.isFinite(dirZ) ? dirZ : Math.cos(r.bodyYaw),
        r.bodyYaw,
        !!headshot
      );
    } catch (err) {
      this._warnOnce('playDeath', err);
    }
  }

  _register(r) {
    const targets = this.ctx.targets;
    if (!targets || typeof targets.register !== 'function' || r.registered) return;
    try {
      // Deathmatch: todo mundo e alvo, entao o remoto entra como TEAM.ENEMY -
      // e o unico time que a bala do jogador local nao poupa.
      targets.register(r, {
        hitboxes: r.hitboxes,
        team: TEAM.ENEMY,
        onDamage: r.onDamage,
        object: r.model?.root || null
      });
      r.registered = true;
    } catch (err) {
      this._warnOnce('targets.register', err);
    }
  }

  _unregister(r) {
    if (!r.registered) return;
    r.registered = false;
    try {
      this.ctx.targets?.unregister?.(r);
    } catch (err) {
      this._warnOnce('targets.unregister', err);
    }
  }

  /**
   * Canal de acerto da Ballistics. NAO aplica dano: quem decide vida e o
   * servidor. Guardar o ultimo acerto serve de diagnostico e evita que a
   * Ballistics caia no fallback `entity.damage()` (que nao existe aqui de
   * proposito - dano local em jogador remoto e exatamente o que nao pode
   * acontecer). Quem transforma isto em reclamacao ao servidor e o NetSync,
   * escutando EVENTS.ENTITY_DAMAGED.
   */
  _onLocalHit(r, amount, info) {
    const h = r.lastHit;
    h.amount = Number.isFinite(amount) ? amount : (Number(info?.amount) || 0);
    h.part = String(info?.part || 'chest');
    h.at = this._time;
    return 0;
  }

  _killerPosition(killerId) {
    if (!Number.isFinite(killerId)) return null;
    const id = killerId | 0;
    if (this._localId !== null && id === this._localId) {
      const p = this.ctx.controller?.position || this.ctx.player?.position;
      return p && Number.isFinite(p.x) ? p : null;
    }
    const other = this._byId.get(id);
    return other ? other.position : null;
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[RemotePlayers] falha em "${key}" - seguindo sem isso:`, err);
  }
}
